import React, { useContext } from 'react'

import {
  Button,
  Checkbox,
  CheckboxProps,
  Dropdown,
  Form,
  Header,
  Icon,
  Image,
  Radio,
  RadioProps,
  Rating,
} from 'semantic-ui-react'

import { IProject, ProjectQuestionResponse } from '../../../api/Data'
import { UserContext } from '../../../contexts/UserContext'

import CollectTabPhoneFrame from './CollectTabPhoneFrame'

const CollectTabCollectPagePreview = ({
  project,
  projectQuestions,
}: {
  project: IProject
  projectQuestions: ProjectQuestionResponse[]
}) => {
  const { userProfile } = useContext(UserContext)
  const {
    displaySetting: { showLocation, showRating, ratingPrompt },
  } = project

  const isResponseWorkflow = ['video_capture', 'video_conversation'].includes(project.projectType)
  const teamName = userProfile?.teamName || 'Your organisation'
  const logoAvatarUrl = userProfile?.logoAvatarUrl
  const projectName = project.name || project.title
  const sharedQuestions = projectQuestions.filter((question) => !question.isAskedPerClip)
  const responseQuestions = projectQuestions.filter((question) => question.isAskedPerClip)

  const wrapQuestionAsField = (question: ProjectQuestionResponse, element: JSX.Element) => (
    <Form.Field key={question.id} required>
      <label>{question.text}</label>
      {element}
    </Form.Field>
  )

  const renderOptionsAsButtons = (
    questionId: number,
    options: string[],
    ButtonComponent: React.ComponentType<RadioProps | CheckboxProps>
  ) => (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '0.5rem',
        marginTop: '0.5rem',
      }}
    >
      {options.map((option) => (
        <ButtonComponent key={option} label={option} name={`question_${questionId}`} value={option} />
      ))}
    </div>
  )

  const renderOptionsAsList = (options: string[], isMultiSelect: boolean) => (
    <Dropdown
      placeholder={isMultiSelect ? 'Select options' : 'Select an option'}
      fluid
      selection
      search
      multiple={isMultiSelect}
      options={options.map((option) => ({ key: option, text: option, value: option }))}
      value={isMultiSelect ? [] : ''}
    />
  )

  const renderProjectQuestion = (question: ProjectQuestionResponse) => {
    if (question.type === 'FreeText') {
      return wrapQuestionAsField(question, <Form.TextArea placeholder="Type here..." name={question.id} rows={2} />)
    }

    if (question.type === 'SingleSelect' || question.type === 'MultiSelect') {
      const isMultiSelect = question.type === 'MultiSelect'
      const options = [...(question.options || []), ...(question.datasetKeys?.map((key) => key.value) || [])]

      if (!options.length) return null

      return wrapQuestionAsField(
        question,
        options.length < 12
          ? renderOptionsAsButtons(question.id, options, isMultiSelect ? Checkbox : Radio)
          : renderOptionsAsList(options, isMultiSelect)
      )
    }

    return null
  }

  return (
    <CollectTabPhoneFrame project={project}>
      <div style={{ padding: '0.25rem' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
            marginBottom: '0.75rem',
          }}
        >
          {logoAvatarUrl && <Image src={logoAvatarUrl} alt={teamName} avatar />}
          <div style={{ textAlign: 'left', lineHeight: 1.2 }}>
            <span
              style={{
                display: 'block',
                fontSize: '0.55rem',
                fontWeight: 700,
                textTransform: 'uppercase',
              }}
            >
              {isResponseWorkflow ? 'Submit content to' : 'Send files to'}
            </span>
            <strong style={{ fontSize: '0.72rem' }}>{teamName}</strong>
          </div>
        </div>

        <Header as="h3" textAlign="center" style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>
          {projectName}
        </Header>
        <Header as="h4" textAlign="center" style={{ marginTop: 0 }}>
          {isResponseWorkflow ? 'Response details' : 'File details'}
        </Header>

        <Form size="mini">
          <Form.Field required>
            <label>Contributor Full Name</label>
            <Form.Input placeholder="e.g. Lauren Hudson" />
          </Form.Field>

          {sharedQuestions.length > 0 && (
            <section
              style={{
                margin: '0.75rem 0',
                padding: '0.75rem',
                border: '1px solid #e4e4e4',
                borderRadius: '8px',
                background: '#fff',
              }}
            >
              <Header as="h5" style={{ marginTop: 0 }}>
                Details for all files
              </Header>
              {sharedQuestions.map(renderProjectQuestion)}
            </section>
          )}

          <section
            style={{
              padding: '0.75rem',
              border: '1px solid #e4e4e4',
              borderRadius: '8px',
              background: '#fff',
            }}
          >
            <div
              style={{
                width: '100%',
                aspectRatio: '16 / 9',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '0.75rem',
                borderRadius: '6px',
                background: '#111',
                color: '#fff',
              }}
            >
              <Icon name="play circle outline" size="huge" style={{ margin: 0 }} />
            </div>

            <Header as="h4" style={{ margin: '0 0 0.25rem' }}>
              {isResponseWorkflow ? 'Response 1' : 'File 1 details'}
            </Header>
            {isResponseWorkflow && project.questions?.[0] && <p>{project.questions[0]}</p>}
            <p style={{ color: '#777', fontSize: '0.68rem' }}>sample-video.mp4</p>

            {showLocation && (
              <Form.Field required>
                <label>Your location</label>
                <Form.Input placeholder="Type here..." />
              </Form.Field>
            )}

            {showRating && (
              <Form.Field required>
                <label>{ratingPrompt}</label>
                <div style={{ margin: '1rem 0', textAlign: 'center' }}>
                  <Rating icon="star" defaultRating={4} maxRating={5} size="large" />
                </div>
              </Form.Field>
            )}

            {responseQuestions.map(renderProjectQuestion)}
          </section>

          <Button fluid compact size="mini" primary style={{ margin: '1rem auto 0' }}>
            {isResponseWorkflow ? 'Submit response' : 'Submit all files'}
          </Button>
        </Form>
      </div>
    </CollectTabPhoneFrame>
  )
}

export default CollectTabCollectPagePreview
